import React, { useMemo } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTheme } from '../../contexts/ThemeContext';
import useIsMobile from '../../hooks/useIsMobile';
import ScrollPickerSelector from './ScrollPickerSelector';

/** Same intervals as the trade chart. Hourly values are minutes for the bars API. */
export const CHART_INTERVAL_OPTIONS = [
  { display: '1 Min', interval: 1, unit: 'Minute' },
  { display: '2 Min', interval: 2, unit: 'Minute' },
  { display: '3 Min', interval: 3, unit: 'Minute' },
  { display: '5 Min', interval: 5, unit: 'Minute' },
  { display: '15 Min', interval: 15, unit: 'Minute' },
  { display: '30 Min', interval: 30, unit: 'Minute' },
  { display: '1 Hour', interval: 60, unit: 'Minute' },
  { display: '2 Hour', interval: 120, unit: 'Minute' },
  { display: '4 Hour', interval: 240, unit: 'Minute' },
  { display: '1 Day', interval: 1, unit: 'Daily' },
  { display: '1 Week', interval: 1, unit: 'Weekly' },
  { display: '1 Month', interval: 1, unit: 'Monthly' },
];

export function barsbackForInterval(interval, unit) {
  if (unit === 'Daily') return 252;
  if (unit === 'Weekly') return 104;
  if (unit === 'Monthly') return 60;
  if (interval >= 240) return 120;
  if (interval >= 60) return 180;
  return 200;
}

function intervalKey(option) {
  return `${option.interval}-${option.unit}`;
}

const ChartIntervalControl = (props) => {
  const { colors } = useTheme();
  const {
    interval = 5,
    unit = 'Minute',
    setIntervalAndUnit,
    sessionTemplate,
    setSessionTemplate,
    showSessionToggle = true,
  } = props;

  const intervalOptions = CHART_INTERVAL_OPTIONS;
  const isMobile = useIsMobile(768, 1024);

  const currentOption = useMemo(() => (
    intervalOptions.find((option) => option.interval === interval && option.unit === unit)
    || intervalOptions[3]
  ), [interval, unit, intervalOptions]);

  const handleIntervalSelect = (selectedOption) => {
    if (selectedOption && setIntervalAndUnit) {
      setIntervalAndUnit(selectedOption.interval, selectedOption.unit);
    }
  };

  const isExtended = sessionTemplate === 'USEQPreAndPost';
  const toggleSession = () => {
    if (!setSessionTemplate) return;
    setSessionTemplate(isExtended ? 'Default' : 'USEQPreAndPost');
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <ScrollPickerSelector
        items={intervalOptions}
        value={currentOption}
        onChange={handleIntervalSelect}
        getItemKey={intervalKey}
        getItemLabel={(item) => item.display}
        showDropdown
        ariaLabel="Chart interval"
        testId="chart-interval-control"
        placeholder="5 Min"
        buttonStyle={{
          padding: '0px 4px',
          minWidth: isMobile ? 78 : 84,
          flexShrink: 0,
          justifyContent: 'center',
        }}
      />

      {showSessionToggle && setSessionTemplate && (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{isExtended ? 'Extended Hours' : 'Regular Hours'}</Tooltip>}
        >
          <Button
            variant="link"
            onClick={toggleSession}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: isExtended ? colors.primary : colors.textLight,
              textDecoration: 'none',
              padding: isMobile ? '10px 10px' : '8px 10px',
              fontSize: isMobile ? '13px' : '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s ease',
              minWidth: '56px',
              justifyContent: 'center',
              boxShadow: 'none',
              outline: 'none',
              flexShrink: 0,
            }}
          >
            {isExtended ? 'EH' : 'RH'}
          </Button>
        </OverlayTrigger>
      )}
    </div>
  );
};

export default ChartIntervalControl;
